import React, { useCallback, useMemo } from "react";
import styled from "styled-components";
import { Commitment, Deal } from "@ifgengineering/client-invest-sdk";
import { ThemedText } from "@components/ThemedText";
import { useTheme } from "styled-components";
import { PortfolioInvestmentCard } from "../PortfolioInvestmentCard";
import DotIcon from "@icon-park/react/es/icons/Dot";
import { navigate } from "gatsby";
import ArrowRightIcon from "@icon-park/react/es/icons/ArrowRight";

interface PortfolioInvestmentsSectionProps {
  originalCommitments: Commitment[];
  filteredCommitments: Commitment[];
  deals: Deal[];
}

const isActive = (commitment: Commitment) => (commitment.currentValue ?? 0) > 0;

export const PortfolioInvestmentsSection = ({
  originalCommitments,
  filteredCommitments,
  deals,
}: PortfolioInvestmentsSectionProps) => {
  const theme = useTheme();
  if (originalCommitments.length === 0) {
    return (
      <ExploreDealsContainer>
        <ExploreDealsTextContainer onClick={() => navigate("/app/deals")}>
          <ThemedText type="S18" color="BLUE600">
            Explore our current deals
          </ThemedText>
          <ArrowRightIcon size={24} fill={theme.colors.ui.icon.brand} />
        </ExploreDealsTextContainer>
      </ExploreDealsContainer>
    );
  }

  const shouldDisplayEntityBadge = useCallback(
    (dealId: string, commitments: Commitment[]) =>
      commitments.filter((commitment) => commitment.dealId === dealId).length >
      1,
    [originalCommitments]
  );

  const sortedCommitments = useMemo(
    () =>
      [...filteredCommitments].sort((a, b) => {
        const aActive = isActive(a);
        const bActive = isActive(b);
        if (aActive === bActive) return 0;
        return aActive ? -1 : 1;
      }),
    [filteredCommitments]
  );

  return (
    <Container>
      <HeaderContainer>
        <HeaderTextsContainer>
          <ThemedText type="T18" color="BLACK">
            Investments{" "}
          </ThemedText>
          <DotIcon size={16} fill={theme.colors.ui.icon.tertiary} />
          <ThemedText type="P16" color="SLATE600">
            {sortedCommitments.length}
          </ThemedText>
        </HeaderTextsContainer>
      </HeaderContainer>
      <PortfolioInvestmentCardsContainer>
        {sortedCommitments.map((commitment) => {
          const deal = deals.find((deal) => deal.id === commitment.dealId);
          const onCardClick = () => {
            navigate(
              `/app/portfolio/investment/${commitment.id}?referrer=portfolio&dealId=${commitment.dealId}`
            );
          };
          const displayEntityBadge = shouldDisplayEntityBadge(
            commitment.dealId,
            originalCommitments
          );
          return (
            <PortfolioInvestmentCard
              key={commitment.id}
              commitment={commitment}
              deal={deal}
              displayEntityBadge={displayEntityBadge}
              onClick={onCardClick}
            />
          );
        })}
      </PortfolioInvestmentCardsContainer>
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const HeaderContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const HeaderTextsContainer = styled.div`
  display: flex;
  gap: 10px;
  align-items: center;
`;

export const PortfolioInvestmentCardsContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 24px;
`;

const ExploreDealsContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 250px;
  width: 100%;
  border-radius: 16px;
  background-color: ${({ theme }) => theme.colors.ui.background.raised};
`;

const ExploreDealsTextContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  cursor: pointer;
  padding: 16px;
`;
